import { ScrollableArea } from "@nova/component-scrollable-area"
import { IconPackOption } from "@nova/constants"
import { iconsByIconPack } from "../constants"
import { InspectorPanelIconPackIcon } from "./InspectorPanelIconPackIcon"
import { useAppDispatch } from "integrations/redux/hooks"
import { updateIcon } from "features/pubsub/entityActions/cubes/icon"
import { updateIconTemplate } from "plugins/Toolbox/tool-registry"
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IconPackIcon } from "@nova/component-map-icons/src/icon-packs"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import * as Icons from "@nova/component-icons"
import { ClickAwayListener } from "@nova/component-click-away-listener"

export const InspectorPanelIconPackDropdown = ({
    selectedIconPack,
    iconId,
    iconUrl,
}: {
    selectedIconPack: Exclude<IconPackOption, "Shape">
    iconId: string | undefined
    iconUrl: string
}) => {
    const dispatch = useAppDispatch()
    const searchRef = useRef<HTMLInputElement>(null)

    const [searchContents, setSearchContents] = useState<string>()
    const [searchBarIsActive, setSearchBarIsActive] = useState(false)
    const [isHoveringSearchBar, setIsHoveringSearchBar] = useState(false)

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Enter" && searchBarIsActive) {
                e.stopPropagation()
                setSearchBarIsActive(false)
            } else if (e.key === "Escape" && searchBarIsActive) {
                e.stopPropagation()
                setSearchBarIsActive(false)
                setSearchContents(undefined)
            }
        },
        [searchBarIsActive]
    )

    const filteredIconPack = useMemo(() => {
        if (!searchContents) return Object.entries(iconsByIconPack[selectedIconPack])

        const filteredSections = Object.entries(iconsByIconPack[selectedIconPack]).filter(
            ([, icons]) =>
                Object.values(icons).some((icon) =>
                    icon.name.toLowerCase().includes(searchContents.toLowerCase())
                )
        )

        filteredSections.forEach(([, icons], i) => {
            filteredSections[i][1] = Object.entries(icons)
                .filter(([, icon]) =>
                    icon.name.toLowerCase().includes(searchContents.toLowerCase())
                )
                .reduce(
                    (acc, [key, icon]) => {
                        acc[key] = icon
                        return acc
                    },
                    {} as Record<string, IconPackIcon>
                )
        })

        return filteredSections
    }, [selectedIconPack, searchContents])

    useEffect(() => {
        if (searchBarIsActive) searchRef.current?.focus()
    }, [searchBarIsActive])

    useEffect(() => {
        if (searchContents === undefined) {
            setSearchBarIsActive(false)
            searchRef.current?.blur()
        }
    }, [searchContents])

    const hasContents = searchContents?.length || 0 > 0

    return (
        <div className="p-1 shadow-card rounded-xl bg-gray-700">
            {/* Search Bar */}
            <ClickAwayListener onClickAway={() => setSearchBarIsActive(false)}>
                <div
                    className={classNames(
                        "mt-4 mx-3 h-8 py-1.5 pl-2.5 flex gap-2 items-center rounded-lg cursor-text border",
                        animationClass,
                        {
                            "border-gray-500": true,
                            "pr-3": !hasContents && !searchBarIsActive,
                            "pr-1.5 bg-gray-500 text-gray-0": hasContents || searchBarIsActive,
                            "bg-gray-900": !searchBarIsActive && !hasContents,
                            "text-gray-300":
                                !isHoveringSearchBar && !searchBarIsActive && !hasContents,
                            "text-gray-100":
                                isHoveringSearchBar && !searchBarIsActive && !hasContents,
                            // "text-gray-0 bg-gray-500": isActive,
                        }
                    )}
                    onMouseEnter={() => setIsHoveringSearchBar(true)}
                    onMouseLeave={() => setIsHoveringSearchBar(false)}
                    onClick={() => setSearchBarIsActive(true)}
                >
                    {/* Magnifying Glass */}
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <Icons.SmallIcon
                            Icon={Icons.MagnifyingGlass}
                            className={classNames({
                                "text-blue-500": searchBarIsActive || hasContents,
                            })}
                        />
                    </div>

                    {/* Text Input or Placeholder */}
                    {!searchBarIsActive ? (
                        <h5 className="flex-grow min-w-0 overflow-hidden whitespace-nowrap text-ellipsis select-none cursor-text">
                            {hasContents ? searchContents : "Search for an icon"}
                        </h5>
                    ) : (
                        <input
                            className={classNames(
                                "p-0 bg-transparent border-0 !shadow-none min-w-0 flex-grow",
                                "focus:outline-none focus:border-transparent focus:ring-0",
                                "text-sm" // <small> text style
                            )}
                            ref={searchRef}
                            type="text"
                            value={searchContents}
                            onChange={(e) => {
                                const sanitizedContents = e.target.value.trimStart()
                                setSearchContents(sanitizedContents)
                            }}
                            onKeyDown={handleKeyDown}
                        />
                    )}

                    {/* Clear-All Button */}
                    {hasContents && (
                        <div
                            className={classNames(
                                "flex-shrink-0 rounded-full text-gray-100 cursor-pointer w-5 h-5 flex items-center justify-center",
                                "hover:bg-gray-700 hover:text-gray-0",
                                animationClass
                            )}
                            onClick={(e) => {
                                e.stopPropagation()
                                setSearchContents(undefined)
                            }}
                        >
                            <div className="absolute">
                                <Icons.ExtraSmallIcon Icon={Icons.XClose} />
                            </div>
                        </div>
                    )}
                </div>
            </ClickAwayListener>

            {/* Icon Grid */}
            {filteredIconPack.length > 0 ? (
                <ScrollableArea className="max-h-[31.25rem] py-4 px-3 flex flex-col gap-3">
                    {filteredIconPack.map(([sectionTitle, icons], i) => (
                        <div key={i} className="flex flex-col gap-3">
                            {/* Icon Section Title */}
                            <h5 className="text-gray-0 select-none">{sectionTitle}</h5>

                            {/* Icon Grid for This Section */}
                            <div className="grid grid-cols-[repeat(6,32px)] justify-between gap-y-5">
                                {Object.entries(icons).map(([id, { name, url }], j) => (
                                    <InspectorPanelIconPackIcon
                                        key={j}
                                        id={id}
                                        name={name}
                                        url={url}
                                        index={j}
                                        isSelected={iconUrl === url}
                                        onSelect={() => {
                                            if (!!iconId)
                                                dispatch(
                                                    updateIcon({
                                                        id: iconId,
                                                        updates: { url, name },
                                                    })
                                                )
                                            dispatch(updateIconTemplate({ url, name }))
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </ScrollableArea>
            ) : (
                <div className="my-4 flex flex-col gap-2 items-center">
                    <h3 className="text-gray-0 font-medium">No results found</h3>
                    <small className="text-gray-200">Try adjusting your search terms.</small>
                </div>
            )}
        </div>
    )
}
